import { initializeApp } from 'firebase/app';
import {
  AuthError,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  getAuth,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import { getRemoteConfig } from 'firebase/remote-config';
import React, { createContext, useLayoutEffect, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { NetworkState } from 'src/constants/network-state';
import { IUserSlice } from 'src/redux/user/userSlice.contracts';

import { useAppDispatch } from '../../../hooks/hooks';
import { setUserStorageOwner } from '../../../utils/userStorage';
import { AuthContextInterface } from '../../context/AuthContextInterface';
import { persistAuth } from '../../utils/persistAuth';
import { resetUser } from '../../utils/resetUser';
import firebaseConfig from './config/firebaseConfig';

const app = initializeApp(firebaseConfig.config);
const remoteConfig = getRemoteConfig(app);

remoteConfig.settings = {
  minimumFetchIntervalMillis:
    process.env.REACT_APP_DEPLOYMENT_ENV === 'development'
      ? 30000 // 30 seconds for development
      : 3600000, // 1 hour for production (default)
  fetchTimeoutMillis: 60000, // 1 minute timeout
};

export { remoteConfig };

export const FirebaseAuthContext = createContext<AuthContextInterface>({
  user: null,
  loading: false,
  error: undefined,
  isAuthenticated: false,
  getTokens: () => new Promise((resolve) => null),
  loginWithEmailAndPassword: (email: string, password: string) =>
    new Promise((resolve) => {}),
  loginWithGoogle: () => new Promise((resolve) => {}),
  register: (email: string, password: string) => new Promise((resolve) => {}),
  logout: () => new Promise((resolve) => {}),
  refreshToken: () => new Promise((resolve) => {}),
  verifyEmail: () => new Promise((resolve) => {}),
  resetPassword: (email: string) => new Promise((resolve) => {}),
  emailVerified: false,
  authHook: undefined,
});

const handleError = (error: AuthError) => {
  toast.error(
    'Authentication Error. Either your email and password was incorrect or there is a network error!'
  );
  console.log('Authentication Error:', error);
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const logoutFirebase = async () => {
  const auth = getAuth();
  await signOut(auth);
  // lazy loaded to avoid circular dependencies caused by:
  // App.tsx -> abilityRoleSlice -> this -> store -> abilityRoleSlice -> ...
  const { default: store } = await import('../../../redux/store');
  store.dispatch(resetUser());
};

export const refreshFirebaseToken = async () => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('Cannot get new token');
    }
    return {
      accessToken: await currentUser?.getIdToken(true),
      refreshToken: currentUser?.refreshToken,
    };
  } catch (error) {
    return {
      refreshToken: null,
      accessToken: null,
    };
  }
};

export const FirebaseAuthProvider: React.FC<AuthProviderProps> = ({
  children,
}) => {
  const auth = getAuth(app);
  const [user, loading, error] = useAuthState(auth);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const userId = user?.uid ?? null;
  const [preparedUserId, setPreparedUserId] = useState<
    string | null | undefined
  >(undefined);

  useLayoutEffect(() => {
    if (loading) return;

    if (preparedUserId === userId) return;

    if (preparedUserId !== undefined) {
      window.location.replace(userId ? '/' : '/auth/login');
      return;
    }

    setUserStorageOwner(userId);
    dispatch({ type: 'auth/sessionChanged' });
    setPreparedUserId(userId);
  }, [dispatch, loading, preparedUserId, userId]);

  const loginWithEmailAndPassword = async (email: string, password: string) => {
    try {
      const firebaseUser = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const temp_user: IUserSlice = {
        switchUser: null,
        user: firebaseUser,
        status: NetworkState.AUTHENTICATED,
        accessToken: await firebaseUser.user.getIdToken(),
        refreshToken: firebaseUser.user.refreshToken,
        error: null,
      };
      dispatch(persistAuth({ userAuth: temp_user }));
    } catch (error: any) {
      handleError(error);
    }
  };

  const loginWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (error: any) {
      handleError(error);
    }
  };

  const register = async (email: string, password: string) => {
    try {
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (error: any) {
      handleError(error);
    }
  };

  const logout = async () => {
    try {
      logoutFirebase();
    } catch (error: any) {
      handleError(error);
    }
  };

  const refreshToken = async () => {
    try {
      const res = await refreshFirebaseToken();
    } catch (error: any) {
      handleError(error);
    }
  };

  const getTokens = async () => {
    const currentUser = auth.currentUser;
    try {
      return {
        accessToken: (await currentUser?.getIdToken()) ?? null,
        refreshToken: currentUser?.refreshToken ?? null,
      };
    } catch (error: any) {
      handleError(error);
      return null;
    }
  };

  const verifyEmail = async () => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      try {
        await sendEmailVerification(currentUser);
      } catch (error: any) {
        handleError(error);
      }
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success(
        'The password reset link was successfully sent! Please check your inbox and spam'
      );
    } catch (error: any) {
      handleError(error);
    }
  };

  const emailVerified = true; //auth.currentUser ? auth.currentUser.emailVerified : false;

  const isAuthenticated = user ? true : false;

  const authHook = useAuthState;

  return (
    <FirebaseAuthContext.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        error,
        getTokens,
        loginWithEmailAndPassword,
        loginWithGoogle,
        register,
        logout,
        refreshToken,
        verifyEmail,
        resetPassword,
        emailVerified,
        authHook,
      }}
    >
      {!loading && preparedUserId === userId && (
        <React.Fragment key={userId ?? 'anonymous'}>{children}</React.Fragment>
      )}
    </FirebaseAuthContext.Provider>
  );
};
